import { RefObject, useEffect, useRef } from 'react'

interface IUseOnVisibleOptions {
  /** Distância (px) antes do elemento entrar na viewport para já disparar. */
  rootMargin?: number
  enabled?: boolean
  /**
   * Muda para re-observar o elemento. Necessário porque o IntersectionObserver
   * só reporta mudanças de estado: se o sentinela continua visível depois de
   * anexar uma página, nenhuma entry nova chega. Re-observar entrega o estado
   * atual de novo e a paginação segue sem depender de gesto do usuário.
   */
  resetKey?: unknown
}

/**
 * Dispara `onVisible` sempre que o elemento referenciado estiver visível —
 * inclusive quando ele já nasce visível.
 *
 * Existe porque o `InView` de react-native-intersection-observer só avisa na
 * transição fora -> dentro e só reavalia durante onScroll/onLayout do
 * IOScrollView: quando uma nova página é anexada com o usuário parado no fim da
 * lista, nenhum evento chega e a paginação trava até rolar para cima e voltar.
 */
export function useOnVisible(
  ref: RefObject<Element | null>,
  onVisible: () => void,
  { rootMargin = 300, enabled = true, resetKey }: IUseOnVisibleOptions = {},
) {
  const onVisibleRef = useRef(onVisible)

  useEffect(() => {
    onVisibleRef.current = onVisible
  }, [onVisible])

  useEffect(() => {
    const node = ref.current
    if (!enabled || !node) return
    if (typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onVisibleRef.current()
        }
      },
      { rootMargin: `0px 0px ${rootMargin}px 0px` },
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [ref, rootMargin, enabled, resetKey])
}
